import { useState, useEffect, useRef } from 'react';

const MINT_START_BLOCK = 907205;
const MINT_INTERVAL = 144;
const POLL_MS = 30000;
const POLL_NEAR_MS = 10000;   // ≤2 blocks before the mint window - the status is time-critical

export function useBlockHeight() {
  const [blockHeight, setBlockHeight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const heightRef = useRef(null);

  const fetchBlockHeight = async () => {
    try {
      // loading flag only for the very first fetch - background polls must not
      // flicker the window board back to '…' every cycle
      if (heightRef.current === null) setLoading(true);
      setError(null);

      // Primary: metashrew_height via our own /api/rpc (sandshrew) - the exact node
      // every alkanes_simulate read already goes through, so the mint-window math
      // and the Fireball round-phase judged against it stay consistent with what
      // the contract itself sees, with zero extra external host in the path.
      // Fallback: espo's get_espo_height (alkanode, a different host/network path).
      // It's its own INDEXED tip and can lag the chain by ~1 block, which would
      // skew the time-critical 144-block card-mint window board / canMintNow
      // check - so it's only used when the primary node is down, where an
      // approximate height beats an indefinite stall.
      const rpcHeight = async () => {
        const r = await fetch('/api/rpc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'metashrew_height', params: [] }),
          signal: AbortSignal.timeout(10_000),
        });
        if (!r.ok) throw new Error('metashrew_height failed');
        const data = await r.json();
        const h = typeof data?.result === 'string' ? parseInt(data.result, 10) : data?.result;
        if (typeof h !== 'number' || Number.isNaN(h)) throw new Error('metashrew_height: bad result');
        return h;
      };
      const espoHeight = async () => {
        const r = await fetch('/api/alkanode', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'get_espo_height', params: [] }),
          signal: AbortSignal.timeout(10_000),
        });
        if (!r.ok) throw new Error('get_espo_height failed');
        const data = await r.json();
        if (typeof data?.result?.height !== 'number') throw new Error('get_espo_height: bad result');
        return data.result.height;
      };

      let height;
      try {
        height = await rpcHeight();
      } catch (primaryErr) {
        height = await espoHeight().catch(() => { throw primaryErr; });
      }
      heightRef.current = height;
      setBlockHeight(height);
    } catch (err) {
      console.error('Error fetching block height:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMintInfo = () => {
    if (!blockHeight) return null;

    const blocksSinceMintStart = blockHeight - MINT_START_BLOCK;
    const currentMintCycle = Math.floor(blocksSinceMintStart / MINT_INTERVAL);
    const currentWindowBlock = MINT_START_BLOCK + currentMintCycle * MINT_INTERVAL;
    const nextWindowBlock = currentWindowBlock + MINT_INTERVAL;

    const canMintNow = blockHeight === nextWindowBlock - 1;
    const blocksUntilNextMint = (nextWindowBlock - 1) - blockHeight;
    const minutesUntilNextMint = blocksUntilNextMint * 10;

    return {
      nextMintBlock: nextWindowBlock,
      hoursUntilNextMint: Math.floor(minutesUntilNextMint / 60),
      minutesUntilNextMint: minutesUntilNextMint % 60,
      canMintNow,
    };
  };

  useEffect(() => {
    let timer;
    let cancelled = false;

    const blocksUntilWindow = () => {
      const h = heightRef.current;
      if (!h) return Infinity;
      const cycle = Math.floor((h - MINT_START_BLOCK) / MINT_INTERVAL);
      const nextWindow = MINT_START_BLOCK + (cycle + 1) * MINT_INTERVAL;
      return (nextWindow - 1) - h;
    };

    // setTimeout chain instead of setInterval: the delay adapts each tick -
    // 10s near the one-block mint window (where 30s of lag matters), 30s otherwise
    const tick = async () => {
      await fetchBlockHeight();
      if (cancelled) return;
      timer = setTimeout(tick, blocksUntilWindow() <= 2 ? POLL_NEAR_MS : POLL_MS);
    };
    tick();

    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  return {
    blockHeight,
    loading,
    error,
    mintInfo: getMintInfo(),
    refetch: fetchBlockHeight
  };
}
